import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { useCompanyPlans } from "@/hooks/useCompanyPlans";
import { Clock, AlertTriangle, CreditCard, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function LicenseExpired() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [systemCode, setSystemCode] = useState<string | null>(null);

  useEffect(() => {
    const company = searchParams.get('company');
    const system = searchParams.get('system');
    setCompanyId(company);
    setSystemCode(system);
  }, [searchParams]);

  const { data: companyPlans, isLoading } = useCompanyPlans(companyId || undefined);

  // Find expired trials
  const expiredTrials = companyPlans?.filter(plan => 
    plan.is_trial && 
    plan.trial_expires_at && 
    new Date(plan.trial_expires_at) < new Date()
  ) || [];

  const handleUpgrade = () => {
    if (companyId) {
      navigate(`/company/${companyId}/plans`);
    }
  };

  const handleBackToAdmin = () => {
    if (companyId) {
      // Get company slug from URL or construct admin path
      const companySlug = window.location.pathname.split('/')[2] || 'admin';
      navigate(`/company/${companySlug}/admin`);
    } else {
      navigate('/admin');
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Verificando licenças...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <AlertTriangle className="mx-auto h-16 w-16 text-amber-500" />
          <h1 className="text-3xl font-bold">Licença Expirada</h1>
          <p className="text-muted-foreground">
            Sua licença de teste expirou. Faça upgrade para continuar usando nossos sistemas.
          </p>
        </div>

        {/* Expired Trials List */}
        {expiredTrials.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Trials Expirados
              </CardTitle>
              <CardDescription>
                Os seguintes sistemas tiveram suas licenças de teste expiradas:
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {expiredTrials.map((plan) => (
                  <div key={plan.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{plan.system_name}</h3>
                        <Badge variant="destructive">Expirado</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Trial de 30 dias • Expirou em{' '}
                        {plan.trial_expires_at && format(
                          new Date(plan.trial_expires_at), 
                          "dd 'de' MMMM 'de' yyyy", 
                          { locale: ptBR }
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Actions */}
        <Card>
          <CardHeader>
            <CardTitle>O que você pode fazer:</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <CreditCard className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="font-medium">Fazer Upgrade para Plano Pago</h4>
                  <p className="text-sm text-muted-foreground">
                    Escolha um plano que atenda às suas necessidades e continue usando todos os recursos.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <ArrowLeft className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="font-medium">Acessar Painel Administrativo</h4>
                  <p className="text-sm text-muted-foreground">
                    Você ainda pode acessar o painel administrativo para gerenciar sua conta e usuários.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={handleBackToAdmin}
            variant="outline"
            className="flex-1"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Admin
          </Button>
          <Button 
            onClick={handleUpgrade}
            className="flex-1"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Ver Planos
          </Button>
        </div>

        {/* Help Text */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            Precisa de ajuda? Entre em contato com nosso suporte em{' '}
            <a href="mailto:suporte@portalonesolucoes.com.br" className="text-primary hover:underline">
              suporte@portalonesolucoes.com.br
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}